import React, { useState } from "react";

const C = {
  bg:        "#09090b",
  surface:   "#18181b",
  surface2:  "#27272a",
  border:    "#27272a",
  borderSub: "#3f3f46",
  text:      "#e4e4e7",
  muted:     "#71717a",
  accent:    "#3b82f6",
  accentH:   "#60a5fa",
  team1:     "#4ade80",
  team2:     "#f87171",
};

const getRankData = (rankStr) => {
  if (!rankStr || rankStr === "Unranked") {
    return { icon: "https://opgg-static.akamaized.net/images/medals_new/unranked.png", text: "Unranked" };
  }
  const parts = rankStr.split(" ");
  return {
    icon: `https://opgg-static.akamaized.net/images/medals_new/${parts[0].toLowerCase()}.png`,
    text: parts[1] || ""
  };
};

// Formate les points en "145K" / "1.2M"
const fmtPts = (n) => {
  if (!n || isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (v >= 1_000)     return `${Math.round(v / 1_000)}K`;
  return String(v);
};

const wrColor = (wr) => wr >= 55 ? C.team1 : wr < 45 ? C.team2 : C.text;

// Couleur + bg du badge niveau maîtrise
const levelStyle = (lvl) => {
  if (lvl >= 7) return { color: "#eab308", bg: "rgba(234,179,8,0.15)",  border: "rgba(234,179,8,0.35)"  };
  if (lvl >= 5) return { color: "#a78bfa", bg: "rgba(167,139,250,0.15)", border: "rgba(167,139,250,0.35)" };
  return           { color: C.muted,     bg: C.surface2,                border: C.border               };
};

// Nombre de jours depuis la date (ex: "3j", "45j")
const fmtDate = (ms) => {
  if (!ms) return "—";
  const days = Math.floor((Date.now() - Number(ms)) / 86_400_000);
  if (days === 0) return "aujourd'hui";
  return `${days}j`;
};

const normalizeRole = (value) => {
  const role = String(value || "").trim().toUpperCase();
  if (["TOP", "TOPLANE"].includes(role)) return "TOP";
  if (["JGL", "JG", "JUNGLE", "JUNGLER"].includes(role)) return "JGL";
  if (["MID", "MIDDLE"].includes(role)) return "MID";
  if (["ADC", "BOT", "BOTTOM"].includes(role)) return "ADC";
  if (["SUP", "SUPPORT", "UTILITY"].includes(role)) return "SUP";
  return "";
};

const getGameRole = (game) => normalizeRole(
  game?.role || game?.lane || game?.teamPosition || game?.individualPosition || game?.position
);

export default function PlayerScoutCard({
  player, onClick, side = "ally",
  selectedChampion, onChampionSelect,
  ddVersion = "14.21.1",
  roleLabel = ""
}) {
  const [view, setView] = useState("champions"); // "champions" | "masteries"
  const [laneOnly, setLaneOnly] = useState(false);

  const games   = player.games || [];
  // Nouveau format : masteries est un tableau d'objets { champion, level, points, lastPlayTime }
  const masteriesArr = Array.isArray(player.masteries) ? player.masteries : [];
  // Lookup dict pour les lignes "Champions récents" : nom → objet mastery
  const masteryMap = Object.fromEntries(masteriesArr.map(m => [m.champion, m]));

  const isAlly = side === "ally";

  const cardBorder    = isAlly ? "1px solid rgba(74,222,128,0.45)"  : "1px solid rgba(248,113,113,0.45)";
  const cardBorderTop = isAlly ? "2px solid rgba(74,222,128,0.8)"   : "2px solid rgba(248,113,113,0.8)";
  const cardBg        = isAlly
    ? "linear-gradient(180deg, #111a12 0%, #09090b 100%)"
    : "linear-gradient(180deg, #1a1111 0%, #09090b 100%)";
  const innerBorder   = isAlly ? "1px solid rgba(74,222,128,0.15)"  : "1px solid rgba(248,113,113,0.15)";
  const accentColor   = isAlly ? C.team1 : C.team2;
  const teamRgb       = isAlly ? "74,222,128" : "248,113,113";

  // ── Vue "Champions récents" ──────────────────────────────────────
  const summarizeChampions = (gamesList) => {
    const map = {};
    for (const game of gamesList) {
      const champ = game.champion || game.championName;
      if (!champ) continue;
      if (!map[champ]) {
        map[champ] = { name: champ, games: 0, wins: 0, masteryPts: masteryMap[champ]?.points ?? "?" };
      }
      map[champ].games += 1;
      if (game.win) map[champ].wins += 1;
    }
    return Object.values(map)
      .map((c) => ({
        ...c,
        winrateValue: Math.round((c.wins / c.games) * 100),
        winrate: `${Math.round((c.wins / c.games) * 100)}%`,
      }))
      .sort((a, b) => b.games !== a.games ? b.games - a.games : b.winrateValue - a.winrateValue);
  };

  const targetRole = normalizeRole(roleLabel || player.role);
  const gamesWithLane = games.filter((game) => getGameRole(game));
  const laneGames = targetRole && gamesWithLane.length
    ? games.filter((game) => getGameRole(game) === targetRole)
    : [];
  const canFilterByLane = Boolean(targetRole && gamesWithLane.length);
  const championGames = laneOnly && canFilterByLane ? laneGames : games;
  const champions         = summarizeChampions(championGames);
  const rankData          = getRankData(player.rank);
  const isAnyChampSelected = selectedChampion !== undefined && selectedChampion !== null;
  const analyzedWins = games.filter((game) => game.win).length;
  const analyzedWinrate = games.length ? Math.round((analyzedWins / games.length) * 100) : null;
  const seasonWins = Number(player.seasonWins || 0);
  const seasonLosses = Number(player.seasonLosses || 0);
  const seasonGames = seasonWins + seasonLosses;
  const seasonWinrate = Number.isFinite(Number(player.seasonWinrate))
    ? Number(player.seasonWinrate)
    : seasonGames
      ? Math.round((seasonWins / seasonGames) * 100)
      : null;
  const seasonLabel = seasonWinrate !== null
    ? `Saison ${seasonWinrate}% WR (${seasonWins}V/${seasonLosses}D)`
    : "Winrate saison indisponible";

  const getChampionRowStyle = (champion) => {
    const wr = champion.winrateValue;
    const g  = champion.games;
    let background = C.surface2;
    let border     = `1px solid ${C.border}`;
    if (isAlly) {
      if (wr >= 51 && g >= 10) { background = "#1a3a2a"; border = "1px solid #2a6b45"; }
      else if (wr >= 51 && g >= 5) { background = "#1a2e3f"; border = "1px solid #2a5070"; }
      else if (wr < 49  && g >= 5) { background = "#3a1f1f"; border = "1px solid #6b3030"; }
    } else {
      if (wr >= 51 && g >= 10) { background = "#3a2510"; border = "1px solid #7a4a20"; }
      else if (wr >= 51 && g >= 5) { background = "#3a3010"; border = "1px solid #7a6020"; }
      else if (wr < 49  && g >= 5) { background = "#3a1f1f"; border = "1px solid #6b3030"; }
    }
    return { background, border };
  };

  // ── Rendu ────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .sc-scroll::-webkit-scrollbar { width: 4px; }
        .sc-scroll::-webkit-scrollbar-track { background: transparent; }
        .sc-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 999px; }
        .sc-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
      `}</style>

      <div
        onClick={onClick}
        style={{
          background: cardBg, border: cardBorder, borderTop: cardBorderTop,
          borderRadius: "14px", padding: "14px", color: C.text,
          height: "350px", cursor: "pointer",
          display: "flex", flexDirection: "column",
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          transition: "transform 0.18s ease, box-shadow 0.18s ease",
          overflow: "hidden",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-3px)";
          e.currentTarget.style.boxShadow = `0 12px 28px rgba(0,0,0,0.6), 0 0 20px rgba(${teamRgb},0.08)`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.5)";
        }}
      >
        {/* ── HEADER ── */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"10px", flexShrink:0, gap:"8px" }}>
          <div style={{ minWidth:0, display:"flex", flexDirection:"column" }}>
            <h3 style={{ margin:0, fontSize:"15px", fontWeight:"800", color:"#f4f4f5", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
              {player.gameName} <span style={{ color:C.muted, fontSize:"12px", fontWeight:"400" }}>#{player.tagLine}</span>
            </h3>
            <div style={{ display:"flex", alignItems:"center", gap:"5px", marginTop:"4px" }}>
              <img src={rankData.icon} alt={player.rank}
                draggable={false}
                style={{ width:"18px", height:"18px", objectFit:"contain" }}
                onError={(e)=>{ e.target.style.display="none"; }} />
              <span style={{ fontSize:"11px", color:C.muted, fontWeight:"600" }}>{rankData.text||"Unranked"}</span>
              {roleLabel && (
                <span style={{ fontSize:"10px", color:accentColor, fontWeight:"900", border:`1px solid rgba(${teamRgb},0.35)`, background:`rgba(${teamRgb},0.10)`, borderRadius:"5px", padding:"2px 5px", lineHeight:1 }}>
                  {roleLabel}
                </span>
              )}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"4px", flexShrink:0 }}>
            <div style={{ fontSize:"11px", fontWeight:"700", color:accentColor, background:`rgba(${teamRgb},0.1)`, border:`1px solid rgba(${teamRgb},0.25)`, padding:"4px 8px", borderRadius:"6px", whiteSpace:"nowrap" }}>
              {player.gamesCount}g
            </div>
            <div style={{ fontSize:"10px", fontWeight:"800", color: analyzedWinrate !== null ? wrColor(analyzedWinrate) : C.muted, whiteSpace:"nowrap" }}>
              Analyse {analyzedWinrate !== null ? `${analyzedWinrate}% WR` : "WR -"}
            </div>
          </div>
        </div>

        {/* ── ZONE PRINCIPALE ── */}
        <div style={{ background:"#0c0c0e", border:innerBorder, borderRadius:"10px", padding:"10px", display:"flex", flexDirection:"column", flex:1, minHeight:0, overflow:"hidden" }}>

          {/* Toggle "Champions récents | Maîtrises" */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ display:"flex", marginBottom:"8px", flexShrink:0, borderBottom:`1px solid ${C.border}`, paddingBottom:"6px" }}
          >
            {[["champions","Champions récents"],["masteries","Maîtrises"]].map(([id,label])=>(
              <button
                key={id}
                onClick={(e)=>{ e.stopPropagation(); setView(id); }}
                style={{
                  flex:1, padding:"3px 0", fontSize:"10px", fontWeight:"700",
                  cursor:"pointer", background:"transparent", border:"none",
                  borderBottom: view===id ? `2px solid ${C.accent}` : "2px solid transparent",
                  color: view===id ? C.accentH : C.muted,
                  transition:"color 0.15s, border-color 0.15s",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── VUE CHAMPIONS RÉCENTS ── */}
          {view==="champions" && (
            <>
              <label
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  marginBottom: "7px",
                  padding: "5px 7px",
                  borderRadius: "7px",
                  border: `1px solid ${canFilterByLane ? `rgba(${teamRgb},0.22)` : C.border}`,
                  background: canFilterByLane ? `rgba(${teamRgb},0.07)` : "rgba(39,39,42,0.45)",
                  color: canFilterByLane ? C.text : C.muted,
                  fontSize: "10px",
                  fontWeight: "800",
                  cursor: canFilterByLane ? "pointer" : "not-allowed",
                  opacity: canFilterByLane ? 1 : 0.55,
                  flexShrink: 0,
                }}
              >
                <span>{canFilterByLane ? `Lane only ${targetRole}` : "Lane data unavailable"}</span>
                <input
                  type="checkbox"
                  checked={Boolean(laneOnly && canFilterByLane)}
                  disabled={!canFilterByLane}
                  onChange={(e) => setLaneOnly(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ accentColor, cursor: canFilterByLane ? "pointer" : "not-allowed" }}
                />
              </label>

              {/* En-têtes colonnes */}
              <div style={{ display:"grid", gridTemplateColumns:"26px minmax(0,1fr) 46px 32px", gap:"6px", marginBottom:"5px", flexShrink:0 }}>
                <div />
                <div style={{ fontSize:"9px", fontWeight:"700", color:C.muted, textTransform:"uppercase", letterSpacing:"0.05em" }}>Maîtrise</div>
                <div style={{ fontSize:"9px", fontWeight:"700", color:C.muted, textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"center" }}>WR</div>
                <div style={{ fontSize:"9px", fontWeight:"700", color:C.muted, textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"right" }}>G</div>
              </div>

              <div className="sc-scroll" style={{ display:"flex", flexDirection:"column", gap:"5px", overflowY:"auto", overflowX:"hidden", minHeight:0, paddingRight:"3px" }}>
                {champions.length === 0 ? (
                  <div style={{ fontSize:"12px", color:C.muted, padding:"10px 0", textAlign:"center" }}>
                    Aucun champion sur cette lane
                  </div>
                ) : champions.map((champion, i) => {
                  const rowStyle     = getChampionRowStyle(champion);
                  const isSelected   = selectedChampion === champion.name;
                  const rowOpacity   = (isAnyChampSelected && !isSelected) ? 0.28 : 1;
                  const activeBorder = isSelected ? "1px solid #fbbf24" : rowStyle.border;
                  const activeShadow = isSelected ? "0 0 0 1px #fbbf24, 0 0 10px rgba(251,191,36,0.3)" : "none";

                  return (
                    <div
                      key={i}
                      onClick={(e)=>{ e.stopPropagation(); if(onChampionSelect) onChampionSelect(champion.name); }}
                      style={{
                        display:"grid", gridTemplateColumns:"26px minmax(0,1fr) 46px 32px",
                        alignItems:"center", gap:"6px",
                        fontSize:"12px", padding:"6px 8px",
                        background:rowStyle.background, border:activeBorder,
                        boxShadow:activeShadow, opacity:rowOpacity,
                        transform:isSelected?"scale(1.02)":"scale(1)",
                        transition:"all 0.15s ease",
                        borderRadius:"7px", flexShrink:0, minWidth:0, cursor:"pointer",
                      }}
                      onMouseEnter={(e)=>{ if(!isSelected) e.currentTarget.style.filter="brightness(1.2)"; }}
                      onMouseLeave={(e)=>{ e.currentTarget.style.filter="brightness(1)"; }}
                    >
                      <img
                        draggable={false}
                        src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${champion.name}.png`}
                        alt={champion.name} title={champion.name}
                        style={{ width:"26px", height:"26px", borderRadius:"5px", objectFit:"cover", flexShrink:0 }}
                        onError={(e)=>{ e.target.src=`https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${champion.name}_0.jpg`; }}
                      />
                      <span
                        title={`${champion.name} — ${Number(champion.masteryPts).toLocaleString()} pts`}
                        style={{ fontWeight:"700", color:"#eab308", fontSize:"12px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}
                      >
                        {fmtPts(champion.masteryPts)}
                      </span>
                      <span style={{ color:wrColor(champion.winrateValue), textAlign:"center", fontWeight:"700", whiteSpace:"nowrap", flexShrink:0, fontSize:"12px" }}>
                        {champion.winrate}
                      </span>
                      <span style={{ color:C.muted, textAlign:"right", fontWeight:"600", whiteSpace:"nowrap", flexShrink:0, fontSize:"11px" }}>
                        {champion.games}g
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── VUE MAÎTRISES ── */}
          {view==="masteries" && (
            <>
              {/* En-têtes colonnes */}
              <div style={{ display:"grid", gridTemplateColumns:"26px minmax(0,1fr) 26px 50px", gap:"5px", marginBottom:"5px", flexShrink:0 }}>
                <div />
                <div style={{ fontSize:"9px", fontWeight:"700", color:C.muted, textTransform:"uppercase", letterSpacing:"0.05em" }}>Champion</div>
                <div style={{ fontSize:"9px", fontWeight:"700", color:C.muted, textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"center" }}>Niv</div>
                <div style={{ fontSize:"9px", fontWeight:"700", color:C.muted, textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"right" }}>Pts / Date</div>
              </div>

              <div className="sc-scroll" style={{ display:"flex", flexDirection:"column", gap:"4px", overflowY:"auto", overflowX:"hidden", minHeight:0, paddingRight:"3px" }}>
                {masteriesArr.length === 0
                  ? <div style={{ fontSize:"12px", color:C.muted, padding:"10px 0", textAlign:"center" }}>Aucune donnée de maîtrise</div>
                  : masteriesArr.map((m, i) => {
                      const ls = levelStyle(m.level);
                      return (
                        <div
                          key={i}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display:"grid", gridTemplateColumns:"26px minmax(0,1fr) 26px 50px",
                            alignItems:"center", gap:"5px",
                            padding:"6px 8px", borderRadius:"7px",
                            background:C.surface2, border:`1px solid ${C.border}`,
                            flexShrink:0, minWidth:0,
                          }}
                        >
                          <img
                            draggable={false}
                            src={`https://ddragon.leagueoflegends.com/cdn/${ddVersion}/img/champion/${m.champion}.png`}
                            alt={m.champion} title={m.champion}
                            style={{ width:"26px", height:"26px", borderRadius:"5px", objectFit:"cover", flexShrink:0 }}
                            onError={(e)=>{ e.target.src=`https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${m.champion}_0.jpg`; }}
                          />
                          <span style={{ fontSize:"12px", fontWeight:"600", color:"#f4f4f5", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }} title={m.champion}>
                            {m.champion}
                          </span>
                          {/* Badge niveau */}
                          <div style={{ display:"flex", justifyContent:"center" }}>
                            <span style={{ fontSize:"9px", fontWeight:"800", color:ls.color, background:ls.bg, border:`1px solid ${ls.border}`, borderRadius:"4px", padding:"1px 4px", whiteSpace:"nowrap" }}>
                              M{m.level}
                            </span>
                          </div>
                          {/* Points + date empilés */}
                          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"1px" }}>
                            <span style={{ fontSize:"11px", fontWeight:"700", color:"#eab308", whiteSpace:"nowrap" }}>
                              {fmtPts(m.points)}
                            </span>
                            <span style={{ fontSize:"9px", color:C.muted, whiteSpace:"nowrap" }}>
                              {fmtDate(m.lastPlayTime)}
                            </span>
                          </div>
                        </div>
                      );
                    })
                }
              </div>
            </>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div style={{ marginTop:"8px", fontSize:"10px", color:C.muted, borderTop:`1px solid rgba(${teamRgb},0.15)`, paddingTop:"7px", fontWeight:"500", flexShrink:0 }}>
          <span style={{ color: seasonWinrate !== null ? wrColor(seasonWinrate) : C.muted, fontWeight:800 }}>
            {seasonLabel}
          </span>
        </div>
      </div>
    </>
  );
}
